import { Link, useParams } from '@tanstack/react-router';
import { PencilRulerIcon, PinIcon, SquareKanbanIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  isPinned,
  opensOnBoard,
  pinnedFirst,
  useWorkflows,
  workflowDestination,
} from '@/features/workflows';
import { WorkflowLimitMark, useLimitStates } from '@/features/spend-limits';
import { usePreferencesStore } from '@/shared/store/preferences';
import { NAV_WORKFLOW_CAP, capWorkflows } from '../capWorkflows';
import { NAV_ACTIVE_LINE } from '../navItemClasses';

export interface WorkflowsNavSectionProps {
  workspaceId: string;
  /** Collapses to icons only. Driven by viewport width, not persisted. */
  collapsed?: boolean;
  /** Id the Workflows row's disclosure control points at. */
  id?: string;
}

/**
 * The workflow list folded under the left-hand navigation's Workflows row: one
 * entry per workflow in the workspace (BR-002-01), oldest first (BR-002-04,
 * ordered by `useWorkflows`).
 *
 * **Published workflows, and the drafts someone pinned** (spec 174). Spec 094
 * FR-005 listed every draft too, so a new workflow could not seem to vanish;
 * but drafts pile up — experiments, half-written copies — and crowded out the
 * boards people open daily. A draft now joins the list when it is pinned
 * (here or with "Pin to sidebar" on the Workflows page). Nothing is lost: "All
 * workflows (n)" appears whenever the list leaves any out, counting all of
 * them as the badge and the Workflows page do, and the builder a new workflow
 * opens in is where it was created.
 *
 * It is its own component rather than another `PRIMARY_NAV` entry because every
 * item in that list is a fixed address taking only a `workspaceId`, which is what
 * lets the sidebar render them uniformly. These entries are data — one per
 * workflow, arriving and changing with the workspace aggregate — so folding them
 * into that array would mean making the static nav list async for the sake of one
 * section.
 *
 * Reading through `useWorkflows` (the same cached aggregate the rest of the app
 * uses) is what makes BR-002-03 and BR-002-07 fall out for free: a workflow
 * imported or renamed elsewhere in the session updates this list with no reload,
 * because there is one cache entry behind both surfaces rather than two that
 * could drift.
 */
export function WorkflowsNavSection({
  workspaceId,
  collapsed = false,
  id,
}: WorkflowsNavSectionProps) {
  const { workflows: all, isPending, loadFailed } = useWorkflows(workspaceId);
  // Read once for the whole list, applied per row: the resolution is pure, so a
  // sidebar of thirty workflows costs the same two reads one board costs.
  const limits = useLimitStates(workspaceId);
  /*
    Pinned first (spec 094 follow-up).

    A cap was the other answer to a long list and it was the wrong one: capping at
    N buries whichever workflow you use daily if it happens to sort N+1, which
    turns the shortcut into a list you scroll past. Pinning lets the person say
    which ones matter instead of the ordering guessing.

    It reorders and never filters, so the list still holds every workflow — which
    is what lets the badge, this list and the root page keep agreeing, and what
    makes having no cap safe: someone who pins nothing sees exactly what they saw
    before.
  */
  const pinnedIds = usePreferencesStore((state) => state.pinnedWorkflowIds);
  const togglePinnedWorkflow = usePreferencesStore(
    (state) => state.togglePinnedWorkflow,
  );
  // A draft waits on the Workflows page until someone pins it (spec 174).
  const listed = all.filter(
    (workflow) => opensOnBoard(workflow) || isPinned(workflow.id, pinnedIds),
  );
  const ordered = pinnedFirst(listed, pinnedIds);
  /*
    At most six, then "All workflows" (spec 128 R3). Spec 094 turned a cap
    down because it buried whichever workflow sorted past it; this one keeps
    the ones that matter to this person — pinned, then the ones this device
    opened last, then the one on screen — so the cap only ever drops what they
    have not touched. With six or fewer, nothing changes.
  */
  const recentKeys = usePreferencesStore(
    (state) => state.recentWorkflowKeys[workspaceId],
  );
  const { workflowKey: currentKey } = useParams({ strict: false });
  const workflows =
    ordered.length <= NAV_WORKFLOW_CAP
      ? ordered
      : capWorkflows(ordered, {
          pinnedIds,
          recentKeys: recentKeys ?? [],
          currentKey,
        });
  const hiddenCount = all.length - workflows.length;

  // Collapsed to icons, per-board entries have nothing to show but identical
  // glyphs — the section header link in PRIMARY_NAV already covers the icon
  // affordance, and this list would be an unlabelled column of duplicates.
  if (collapsed) return null;

  // While pending the section renders nothing at all: BR-002-02 forbids a
  // spinner here, and nothing is shown until the answer is known, so the section
  // never claims an emptiness it has not verified.
  //
  // Stated here rather than beside the `isPending` branch below because a
  // comment inside that ternary chain is not idempotent under Prettier — it gets
  // hoisted and re-wrapped differently on each run, so `format:check` fails
  // however many times `format` is run.
  return (
    <div className="flex flex-col gap-0.5">
      {loadFailed ? (
        // Distinct from "no workflows": inviting the user to create their first
        // workflow when the read merely failed would be a lie about their data.
        <p className="text-muted-foreground px-2.5 py-1 text-xs">
          Workflows could not be loaded.
        </p>
      ) : isPending ? null : all.length === 0 ? (
        <div className="flex flex-col items-start gap-1 px-2.5 py-1">
          <p className="text-muted-foreground text-xs">No workflows yet.</p>
          <Link
            to="/workspaces/$workspaceId/workflows"
            params={{ workspaceId }}
            className="text-sidebar-foreground/80 hover:text-sidebar-accent-foreground focus-visible:ring-sidebar-ring rounded-sm text-xs underline underline-offset-4 focus-visible:ring-3 focus-visible:outline-none"
          >
            Add your first workflow
          </Link>
        </div>
      ) : workflows.length === 0 ? null : (
        /* Not a scrolling region of its own. It was one — bounded, so a long
           list could not push the rest of the nav away — but that put a second
           scrollbar inside the first, and scrolling the inner one while the
           outer stayed still reads as a fault. The sidebar scrolls as one
           column, and a workspace with thirty workflows folds them away instead
           (the Workflows row's disclosure). */
        <ul
          id={id}
          data-testid="workflows-nav-list"
          className="flex flex-col gap-0.5"
        >
          {workflows.map((workflow) => {
            const pinned = isPinned(workflow.id, pinnedIds);
            return (
              <li key={workflow.id} className="group/row relative">
                {/* Where the workflow can actually be used: published to its board,
                  draft to its builder. Asked of `workflowDestination` rather than
                  decided here, so this row, the list page, the palette and the
                  `/workflows/{key}` redirect cannot disagree (FR-009). */}
                <Link
                  {...workflowDestination(workflow, workspaceId)}
                  className={cn(
                    'group relative flex items-start gap-2.5 rounded-md py-1.5 pr-8 pl-5 text-sm',
                    // The same "you are here" line as every rail entry.
                    NAV_ACTIVE_LINE,
                    'text-sidebar-foreground/80 transition-colors',
                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                    'focus-visible:ring-sidebar-ring focus-visible:ring-3 focus-visible:outline-none',
                    'data-[status=active]:bg-sidebar-accent data-[status=active]:text-sidebar-accent-foreground data-[status=active]:font-medium',
                  )}
                >
                  {/* The icon follows the destination: a board for a published
                    workflow, the builder's tool for a draft. It accompanies the
                    word below and never replaces it. */}
                  {opensOnBoard(workflow) ? (
                    <SquareKanbanIcon
                      aria-hidden="true"
                      className="mt-0.5 size-3.5 shrink-0"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <PencilRulerIcon
                      aria-hidden="true"
                      className="mt-0.5 size-3.5 shrink-0"
                      strokeWidth={1.5}
                    />
                  )}
                  {/*
                  The workflow's current name, so a rename re-labels the entry
                  without a reload (BR-002-07).

                  **Two lines, not one with an ellipsis.** At this width a single
                  line cut most real names mid-word — "Spec-Driven Develop…",
                  "Field Errors D…" — and the sidebar is where someone picks
                  between workflows, which is exactly when the distinguishing part
                  of a name tends to be at the end.

                  Wrapping rather than a hover treatment on purpose: a marquee or
                  a tooltip alone shows nothing to a touch or keyboard user, and
                  the guideline for long content is to let it expand, not to
                  animate it. Two lines is the expansion that needs no
                  interaction at all.

                  Still clamped, because a name can be arbitrarily long and this
                  is a navigation rail, not a place to read prose. `title` carries
                  the rest for a pointer; the board's own heading and the
                  breadcrumb carry it for everyone else, so a clipped name here
                  is never a dead end.
                */}
                  <span className="line-clamp-2 min-w-0" title={workflow.name}>
                    {workflow.name}
                  </span>
                  {/* A draft says so, in a word (FR-003). Not by icon alone and not
                    by position: those are the two ways this list could claim the
                    distinction without actually making it.

                    Only a draft (spec 175). A published workflow with unpublished
                    changes carried the same word here (spec 144 FR-010), and in a
                    list of boards it read as "not published". It is a board; the
                    Workflows page and the builder say it has changes waiting. */}
                  {opensOnBoard(workflow) ? null : (
                    <span className="text-muted-foreground mt-0.5 shrink-0 text-xs uppercase">
                      Draft
                    </span>
                  )}
                  {/* A board that will not start runs, marked where the boards
                    are listed (spec 101 US4) — so nobody has to open each one
                    to find out which is stopped. */}
                  <WorkflowLimitMark
                    workflowName={workflow.name}
                    state={limits.forWorkflow(workflow.id)}
                  />
                </Link>
                {/*
                The pin, as a control and not only a marker. It sat inside the
                link as a read-only icon, and the only way to *set* it was the
                overflow menu on the Workflows page — which nobody standing in
                the sidebar could find. So: a pinned row shows its pin at rest
                and that pin is the unpin button; an unpinned row reveals the
                same button on hover and, for the keyboard, on focus — the
                button is in the tab order at all times, only its opacity
                changes. Touch has no hover, which is why the list page's menu
                item stays as well: this is a shortcut, not the only door.

                A sibling of the link, not a child: a button inside an anchor
                is not valid markup and the two would fight over the click.
              */}
                <button
                  type="button"
                  aria-pressed={pinned}
                  aria-label={
                    pinned
                      ? `Unpin ${workflow.name} from the top`
                      : `Pin ${workflow.name} to the top`
                  }
                  onClick={() => togglePinnedWorkflow(workflow.id)}
                  className={cn(
                    'absolute top-1/2 right-1.5 -translate-y-1/2 rounded p-0.5 transition-opacity',
                    'text-muted-foreground hover:text-sidebar-accent-foreground',
                    'focus-visible:ring-sidebar-ring focus-visible:opacity-100 focus-visible:ring-3 focus-visible:outline-none',
                    pinned
                      ? 'opacity-100'
                      : 'opacity-0 group-focus-within/row:opacity-100 group-hover/row:opacity-100',
                  )}
                >
                  <PinIcon
                    aria-hidden="true"
                    className={cn('size-3', pinned && 'fill-current')}
                    strokeWidth={1.5}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {hiddenCount > 0 ? (
        <Link
          to="/workspaces/$workspaceId/workflows"
          params={{ workspaceId }}
          className="text-sidebar-foreground/70 hover:text-sidebar-accent-foreground focus-visible:ring-sidebar-ring rounded-md py-1 pl-5 text-xs underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:outline-none"
        >
          All workflows ({all.length})
        </Link>
      ) : null}
    </div>
  );
}
